import { CloseIconButton } from '@src/shared/ui/Button';
import { Box, Stack } from '@src/shared/ui/layout';
import Typography from '@src/shared/ui/Typography';
import type { PropsWithChildren, ReactNode } from 'react';

export type SidePanelContentProps = PropsWithChildren<{
  title?: ReactNode;
  headerContent?: ReactNode;
  headerActions?: ReactNode;
  footer?: ReactNode;
  onClose?: () => void;
  hideCloseButton?: boolean;
}>;

const StyleSheet = {
  root: {
    height: '100%',
    minHeight: 0,
  },
  header: {
    alignItems: 'center',
    flexShrink: 0,
    px: 2.5,
    py: 2.25,
  },
  headerContent: {
    flexShrink: 0,
    px: 2.5,
  },
  headerActions: {
    alignItems: 'center',
    ml: 'auto',
  },
  body: (hasFooter: boolean) => ({
    flexGrow: 1,
    minHeight: 0,
    overflowX: 'hidden',
    overflowY: 'auto',
    pb: hasFooter ? 0 : 2.5,
    px: 2.5,
  }),
  footer: {
    flexShrink: 0,
    px: 2.5,
    py: 1.25,
  },
} as const;

function SidePanelContent({
  title,
  headerContent,
  headerActions,
  footer,
  onClose,
  hideCloseButton = false,
  children,
}: SidePanelContentProps) {
  const showCloseButton = !hideCloseButton && onClose;

  return (
    <Stack sx={StyleSheet.root}>
      {title || showCloseButton ? (
        <Stack direction="row" sx={StyleSheet.header}>
          <Typography variant="h5" component="div" sx={{ lineHeight: '24px' }}>
            {title}
          </Typography>

          {headerActions ? (
            <Stack direction="row" spacing={1} sx={StyleSheet.headerActions}>
              {headerActions}
            </Stack>
          ) : null}

          {showCloseButton ? (
            <CloseIconButton
              onClick={onClose}
              size="small"
              sx={{ ml: headerActions ? 1 : 'auto' }}
            />
          ) : null}
        </Stack>
      ) : null}

      {headerContent ? (
        <Box sx={StyleSheet.headerContent}>{headerContent}</Box>
      ) : null}

      <Box sx={StyleSheet.body(Boolean(footer))}>{children}</Box>

      {footer ? <Box sx={StyleSheet.footer}>{footer}</Box> : null}
    </Stack>
  );
}

export default SidePanelContent;
